import React, { useEffect, useMemo, useState } from 'react';
import { Combobox, Option } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { GuideField } from '../../../theme/components/GuideField';
import { ProtocolPicker } from '../ProviderConsumptions/ProtocolPicker';
import { filterPatients, getApiErrorMessage, patientLabel } from '../../../functions/ProviderConsumptionImport.Functions';
import { ImportAssignment } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { AssignmentOptions } from './useAssignmentOptions';

/** Opciones de paciente que se dibujan (se filtra escribiendo). */
export const MAX_PATIENT_OPTIONS = 100;

export interface PatientPickerProps {
  patients: ProtocolPatient[];
  value: string | null | undefined;
  onChange: (numberInProtocol: string | undefined) => void;
  placeholder: string;
  emptyText: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Combobox de paciente del protocolo ("nro. en protocolo - historia clínica"), con el filtro de
 * `AssignProtocolPatient.Drawer` (`filterPatients` / `patientLabel`). Borrar el texto quita el paciente.
 */
export const PatientPicker = ({ patients, value, onChange, placeholder, emptyText, disabled, 'data-testid': testId }: PatientPickerProps) => {
  const selected = patients.find((patient) => patient.numberInProtocol === value);
  const labelOf = (number: string | null | undefined) => (selected ? patientLabel(selected) : number ?? '');
  const [query, setQuery] = useState(labelOf(value));

  useEffect(() => {
    setQuery(value ? labelOf(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selected?.numberInProtocol]);

  const options = useMemo(
    () => filterPatients(patients, selected && query === patientLabel(selected) ? '' : query).slice(0, MAX_PATIENT_OPTIONS),
    [patients, query, selected]
  );

  return (
    <Combobox
      freeform
      clearable
      placeholder={placeholder}
      value={query}
      selectedOptions={value ? [value] : []}
      disabled={disabled}
      onChange={(event) => {
        setQuery(event.target.value);
        if (value) onChange(undefined);
      }}
      onOptionSelect={(_event, data) => {
        const patient = patients.find((item) => item.numberInProtocol === data.optionValue);
        setQuery(patient ? patientLabel(patient) : '');
        onChange(patient?.numberInProtocol);
      }}
      data-testid={testId}
    >
      {options.length === 0 ? (
        <Option value="" text="" disabled>
          {emptyText}
        </Option>
      ) : (
        options.map((patient) => (
          <Option key={patient.numberInProtocol} value={patient.numberInProtocol} text={patientLabel(patient)}>
            {patientLabel(patient)}
          </Option>
        ))
      )}
    </Combobox>
  );
};

export interface ImportAssignmentFieldsProps {
  options: AssignmentOptions;
  value: ImportAssignment | undefined;
  /** Protocolo obligatorio, paciente opcional; sin protocolo = sin asignación (undefined). */
  onChange: (value: ImportAssignment | undefined) => void;
  disabled?: boolean;
  /** Error de validación del protocolo (diálogo de la fila). */
  protocolError?: string;
  /** Prefijo de los data-testid (`{prefix}-protocol`, `{prefix}-patient`). */
  testIdPrefix: string;
}

/**
 * Protocolo (`ProtocolPicker`) + paciente del protocolo (opcional; MP 031 lo acepta sin paciente). Se usa en la barra
 * "Asignar a seleccionados" y en el diálogo de la fila.
 */
export const ImportAssignmentFields = ({ options, value, onChange, disabled, protocolError, testIdPrefix }: ImportAssignmentFieldsProps) => {
  const { t } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => t(`providerConsumptionImport.review.assignment.${key}`, params) as string;
  const protocolId = value?.protocolId;
  const patients = options.patientsOf(protocolId);
  const { loadPatients } = options;

  useEffect(() => {
    if (protocolId) loadPatients(protocolId);
  }, [protocolId, loadPatients]);

  const protocolHint = (): React.ReactNode => {
    if (options.protocolsStatus === 'loading') return tr('loadingProtocols');
    if (options.protocolsStatus === 'failed') return `${tr('loadProtocolsError')} ${getApiErrorMessage(options.protocolsError)}`.trim();
    return undefined;
  };

  const patientHint = (): React.ReactNode => {
    if (!protocolId) return tr('patientHint');
    if (patients.status === 'loading') return tr('loadingPatients');
    if (patients.status === 'failed') return `${tr('loadPatientsError')} ${getApiErrorMessage(patients.error)}`.trim();
    return tr('patientOptional');
  };

  return (
    <>
      <GuideField label={tr('protocol')} hint={protocolHint()} error={protocolError} required={!!protocolError}>
        <ProtocolPicker
          protocols={options.protocols}
          value={protocolId}
          onChange={(id) => onChange(id ? { protocolId: id, patientNumberInProtocol: null } : undefined)}
          placeholder={tr('protocolPlaceholder')}
          emptyText={tr('noProtocols')}
          disabled={disabled || options.protocolsStatus === 'loading'}
          data-testid={`${testIdPrefix}-protocol`}
        />
      </GuideField>
      <GuideField label={tr('patient')} hint={patientHint()}>
        <PatientPicker
          patients={patients.data}
          value={value?.patientNumberInProtocol}
          onChange={(number) => protocolId && onChange({ protocolId, patientNumberInProtocol: number ?? null })}
          placeholder={tr('patientPlaceholder')}
          emptyText={tr('noPatients')}
          disabled={disabled || !protocolId || patients.status === 'loading'}
          data-testid={`${testIdPrefix}-patient`}
        />
      </GuideField>
    </>
  );
};

export default ImportAssignmentFields;
